import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { EntrataSession } from 'auth/EntrataSession';
import { setIsAuthenticated } from 'state/slices/authSlice';
import { setPermission } from 'state/slices/userPermissionSlice';

import { postUserLogin } from 'services';
import { redirectToLegacyApplication } from 'utils/legacyRedirect';
import { ensureLaunchContextHydrated } from 'utils/viewerLaunchContextHydration';
import { resolveEntrataRedirectLink } from 'utils/viewerLaunchContextSession';

import useAuthRedirection from './useAuthRedirection';

interface LoginRequest {
  orgId: number;
  propertyId: number;
  username: string;
  password: string;
  entrataRedirectLink?: string;
}
const useLoginUser = () => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const authRedirection = useAuthRedirection();

  const { mutateAsync } = useMutation({ mutationFn: postUserLogin });

  const validateRequestData = (data: LoginRequest) => {
    const { orgId, propertyId, username, password } = data;
    if (!orgId) throw new Error('Org. id is missing.');
    if (!propertyId) throw new Error('Property Id is missing.');
    if (!username) throw new Error('Username is missing.');
    if (!password) throw new Error('Password is missing.');
  };

  const loginRequest = async (data: LoginRequest) => {
    const response = await mutateAsync(data);
    return response;
  };

  const loginUser = async (data: LoginRequest) => {
    setIsLoading(true);
    try {
      validateRequestData(data);
      EntrataSession.setSessionExpired(false);
      const response = await loginRequest(data);
      if (response.status >= 200 && response.status < 300) {
        const { applicant, form } = response.data;
        EntrataSession.setSessionOrigin('applicant_self_service');
        EntrataSession.setApplicant(applicant);

        await authRedirection(form, applicant, data.orgId);

        dispatch(setIsAuthenticated(true));
        dispatch(
          setPermission({
            viewApplicationMaskedSsnInfoAction: true,
          }),
        );
      } else {
        throw new Error('Error while login');
      }
    } catch (err: any) {
      // Check for 404 (no LJ application) and redirect to legacy
      if (err.response?.status === 404) {
        const errorData = err.response?.data;

        // Legacy applicant: navigate to the prospect portal when we have org context.
        if (errorData?.orgDomain) {
          await ensureLaunchContextHydrated();
          const entrataRedirectLink =
            data.entrataRedirectLink ||
            resolveEntrataRedirectLink(new URLSearchParams(window.location.search));
          redirectToLegacyApplication({
            orgDomain: errorData.orgDomain,
            propertyId: data.propertyId,
            entrataRedirectLink,
          });
          return; // Don't throw error after redirect
        }
      }
      throw new Error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, loginUser };
};

export default useLoginUser;
